import { ProjectStore } from '../../../services/stores/projects.store';
import { ISearchFilter, SearchFilter } from '../../../models/search-filter.model';
import { StringUtility } from '../../../services/utilities/string.utility';
import { DateHelper } from '../../../services/helpers/date.helper';
import { UserDataHelper } from '../../../services/helpers/user-data.helper';
import { UserStore } from '../../../services/stores/user.store';
import { IWorkspaceInstanceModel } from '../../../models/workspace/workspace-instance.model';
import { WorkspaceStore } from '../../../services/stores/workspace.store';
import { LoggerService } from '../../../services/logger.service';
import { AfterViewInit, ChangeDetectorRef, Component, ViewChild, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BaseComponent } from '../../base/base.component';
import { BlueprintDataHelper } from '../../../services/helpers/blueprint-data.helper';
import * as DefaultConstants from '../../../modules/catch-core/data/constants/default-value.constants';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';

import { FilterService } from '../../../services/filter.service';
import { IFilterConfig } from '../../../models/filter/filter-config.model';
import { IFilterValue } from '../../../models/filter/filter-value.model';
import { FilterOperator } from '../../../models/filter/filter-operator.enum';
import { IFilterState, FilterState } from '../../../models/filter/filter-state.model';

@Component({
  selector: 'app-filter-toolbar',
  templateUrl: './filter-toolbar.component.html',
  styleUrls: ['./filter-toolbar.component.css'],
  standalone: false,
})
export class FilterToolbarComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  // New filter system properties
  public filterState: IFilterState = new FilterState();
  public defaultFilters: IFilterConfig[] = [];
  public moreFilters: IFilterConfig[] = [];
  public activeFilters: IFilterValue[] = [];
  public activeFilterIds: string[] = [];
  public currentActiveFilter: string | null = null;
  public currentOpenDropdown: string | null = null;
  private activeFilterStatus = new Map<string, boolean>();
  @Input() public standalone: boolean = false;
  @Output() public onSearchFilterChange = new EventEmitter<ISearchFilter>();

  // Legacy properties (to be removed gradually)
  @ViewChild('calendar') calendar: any;
  public searchControl = new FormControl('');
  public nodeTypeFilter = new FormControl<string[]>([]);
  public statusFilter = new FormControl<string[]>([]);
  public assigneeFilter = new FormControl<string[]>([]);
  public dueDateFilter!: Date | any;
  public assigneeList: any = [];

  private className = 'FilterToolbarComponent';
  private startDate: any;
  private endDate: any;
  private selectedWorkspace: IWorkspaceInstanceModel = {} as IWorkspaceInstanceModel;
  private destroy$ = new Subject<void>();

  public nodeTypesList: string[] = [];

  constructor(
    private dateHelper: DateHelper,
    private cdr: ChangeDetectorRef,
    private loggerService: LoggerService,
    private workspaceStore: WorkspaceStore,
    private userStore: UserStore,
    private userDataHelper: UserDataHelper,
    private blueprintDataHelper: BlueprintDataHelper,
    private stringUtility: StringUtility,
    private projectStore: ProjectStore,
    private filterService: FilterService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeFilterSystem();
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.applyNewFilter();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFilterSystem(): void {
    // Subscribe to filter state changes
    this.filterService
      .getFilterState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.filterState = state;
        this.defaultFilters = state.defaultFilters;
        this.moreFilters = state.moreFilters;
        this.activeFilters = state.activeFilters;
        this.activeFilterIds = state.activeFilters.map((f) => f.field);
        this.updateActiveFilterStatus();
        this.cdr.detectChanges();
      });
  }

  // New filter system methods
  public onFilterChange(filter: IFilterValue): void {
    if (!filter) {
      return;
    }
    this.filterService.addFilter(filter);
    this.applyNewFilter();
  }

  public onFilterRemove(field: string): void {
    if (!field) {
      return;
    }
    this.filterService.removeFilter(field);
    this.currentActiveFilter = null;
    this.applyNewFilter();
  }

  public onFilterActivate(filterId: string): void {
    if (!filterId) {
      return;
    }
    this.currentActiveFilter = filterId;

    // This is called when user sets a value in the filter
    // The filter will be added via onFilterChange when the value is emitted
    this.cdr.detectChanges();
  }

  public onFilterDropdownToggle(filterId: string): void {
    if (!filterId) {
      return;
    }
    // Set the currently open dropdown, which will close all others
    this.currentOpenDropdown = filterId;
    this.cdr.detectChanges();
  }

  public onAddMoreFilter(filter: IFilterConfig): void {
    if (!filter) {
      return;
    }

    // Don't add if already active
    if (this.isFilterActive(filter.field)) {
      return;
    }

    // Add the filter to active filters with default values
    // It will show as inactive (normal color) until user selects options
    const defaultFilter: IFilterValue = {
      field: filter.field,
      operator: filter.defaultOperator,
      value: filter.multiple ? [] : null,
      label: filter.label,
    };

    this.filterService.addFilter(defaultFilter);
    this.currentActiveFilter = filter.field;

    // Open the dropdown immediately so user can select options
    this.currentOpenDropdown = filter.field;
    this.cdr.detectChanges();
  }

  public onClearAllFilters(): void {
    this.filterService.clearAllFilters();
    this.currentActiveFilter = null;
    this.searchControl.setValue('', { emitEvent: false });
    this.applyNewFilter();
  }

  public onSearchClear(): void {
    this.searchControl.setValue('');
  }

  public isFilterActive(filterId: string): boolean {
    if (!filterId) {
      return false;
    }
    return this.activeFilterStatus.get(filterId) ?? false;
  }

  public getActiveFilterValue(filterId: string): IFilterValue | undefined {
    if (!filterId) {
      return undefined;
    }
    return this.activeFilters.find((f) => f.field === filterId);
  }

  private updateActiveFilterStatus(): void {
    this.activeFilterStatus.clear();
    if (!this.activeFilters || this.activeFilters.length === 0) {
      this.activeFilterIds.forEach((field) => this.activeFilterStatus.set(field, false));
      return;
    }

    const activeFieldSet = new Set(this.activeFilterIds);
    activeFieldSet.forEach((field) => this.activeFilterStatus.set(field, false));

    this.activeFilters.forEach((filterValue) => {
      if (!filterValue) {
        return;
      }
      const value = filterValue.value;
      const hasValue = value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0);

      this.activeFilterStatus.set(filterValue.field, hasValue);
    });
  }

  private applyNewFilter(): void {
    const filter: ISearchFilter = new SearchFilter();

    // Convert new filter system to legacy format for now
    this.activeFilters.forEach((activeFilter) => {
      const operator = activeFilter.operator;
      switch (activeFilter.field) {
        case 'status':
          if (!operator || operator === FilterOperator.EQUALS) {
            filter.status = Array.isArray(activeFilter.value) ? activeFilter.value : [activeFilter.value];
          }
          break;
        case 'nodeType':
          if (!operator || operator === FilterOperator.EQUALS) {
            filter.nodeType = Array.isArray(activeFilter.value) ? activeFilter.value : [activeFilter.value];
          }
          break;
        case 'assignee':
          if (!operator || operator === FilterOperator.EQUALS) {
            filter.assignee = Array.isArray(activeFilter.value) ? activeFilter.value : [activeFilter.value];
          }
          break;
      }
    });

    const rawSearchTerm = this.searchControl?.value;
    if (typeof rawSearchTerm === 'string' && rawSearchTerm.trim().length > 0) {
      filter.name = rawSearchTerm.trim();
    } else {
      filter.name = '';
    }

    // Add new filter system data. Copy rather than alias this.activeFilters — FilterState.addFilter()
    // mutates that array in place, so WorkspaceStore's searchFilter setter (which compares the
    // previous vs. new SearchFilter to decide whether to skip re-filtering) would otherwise see the
    // exact same (already-mutated) array on both sides and wrongly conclude nothing changed.
    filter.filters = [...this.activeFilters];

    this.updateActiveFilterStatus();

    if (this.standalone) {
      this.onSearchFilterChange.emit(filter);
    } else {
      this.workspaceStore.searchFilter = filter;
      if (!this.workspaceStore.hasRawWorkspace() && this.selectedWorkspace?.id) {
        this.loadWorkspace(this.selectedWorkspace.id);
      }
    }
  }

  private resetFilters(): void {
    this.nodeTypeFilter.setValue([]);
    this.statusFilter.setValue([]);
    this.assigneeFilter.setValue([]);
    this.searchControl.setValue('', { emitEvent: false });
    this.dueDateFilter = null;
    this.startDate = null;
    this.endDate = null;
  }

  ngAfterViewInit(): void {
    // Legacy form controls initialization
    this.searchControl.setValue('', { emitEvent: false });
    this.nodeTypeFilter = new FormControl([]);
    this.statusFilter = new FormControl([]);
    this.assigneeFilter = new FormControl([]);
    this.dueDateFilter = null;
    this.startDate = null;
    this.endDate = null;

    // Legacy subscriptions
    if (!this.standalone) {
      this.subscribers.selectedProject = this.projectStore.$selectedProject.subscribe((project) => {
        if (project) {
          this.resetFilters();
          this.filterService.clearAllFilters();
        }
      });
    }

    if (!this.standalone) {
      this.subscribers.filterSubscription = this.workspaceStore.$searchFilter.subscribe((filterValues) => {
        if (filterValues) {
          this.applyFilterValues(filterValues);
        }
      });
    }

    //if (!this.standalone) { may required
    this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((data) => {
      if (data) {
        this.selectedWorkspace = data;
        this.buildNodeTypeList();
        // Update filter service with new node types
        this.filterService.updateNodeTypeOptions(this.nodeTypesList);
      }
    });

    this.subscribers.users = this.userStore.$users.subscribe((u) => {
      if (u && u.count() > 0) {
        let users = this.userDataHelper.removeCatchAdminUsers(u.toArray());
        this.assigneeList = this.userDataHelper.sortUsers(users);
        // Update filter service with new users
        this.filterService.updateAssigneeOptions(users);
      }
    });
  }

  public onFilterChanged(event: any, values: any): void {
    if (!event || !event.source) {
      return;
    }
    if (values && values.includes('None')) {
      if (event.source.id === 'nodeType') {
        this.nodeTypeFilter.setValue([]);
      } else if (event.source.id === 'status') {
        this.statusFilter.setValue([]);
      } else if (event.source.id === 'assignee') {
        this.assigneeFilter.setValue([]);
      }
    }

    this.applyFilter();
  }

  public onClearAllFiltersLegacy(): void {
    if (
      this.statusFilter?.value?.length === 0 &&
      this.nodeTypeFilter?.value?.length === 0 &&
      this.assigneeFilter?.value?.length === 0 &&
      this.searchControl?.value?.length === 0 &&
      this.dueDateFilter === null
    ) {
      return;
    }

    this.resetFilters();
    this.applyFilter();
  }

  public onDueDateSet(): void {
    if (this.dueDateFilter && this.dueDateFilter[0] !== null && this.dueDateFilter[1] !== null) {
      this.startDate = this.dateHelper.convertDateStringToDate(this.dueDateFilter[0]);
      this.endDate = this.dateHelper.convertDateStringToDate(this.dueDateFilter[1]);
      this.applyFilter();
    }
  }

  public onClearDueDateFilter(): void {
    this.onCloseCalendar();
    this.dueDateFilter = null;
    this.startDate = null;
    this.endDate = null;
    this.applyFilter();
  }

  public onCloseCalendar(): void {
    this.calendar.hideOverlay();
    this.cdr.detectChanges();
  }

  private buildNodeTypeList() {
    const nodeTypeList = this.blueprintDataHelper.getNodeTypeNameList(this.blueprintDataHelper.selectedBluePrint);

    if (nodeTypeList) {
      nodeTypeList.push(DefaultConstants.NONE);
      this.nodeTypesList = this.stringUtility.makeCapitalizeEachStringInStringArray(nodeTypeList);
    }
  }

  private applyFilter(): void {
    const filter: ISearchFilter = new SearchFilter();
    if (filter) {
      if (this.statusFilter && this.statusFilter.value && this.statusFilter.value.length > 0) {
        //filter['Status'] = this.statusFilter.value;
        filter.status = this.statusFilter.value;
      }
      if (this.nodeTypeFilter && this.nodeTypeFilter.value && this.nodeTypeFilter.value.length > 0) {
        //filter['NodeType'] = this.nodeTypeFilter.value;
        filter.nodeType = this.nodeTypeFilter.value;
      }
      if (this.assigneeFilter && this.assigneeFilter.value && this.assigneeFilter.value.length > 0) {
        //filter['Assignee'] = this.assigneeFilter.value;
        filter.assignee = this.assigneeFilter.value;
      }
      const searchValue = this.searchControl?.value ?? '';
      if (typeof searchValue === 'string' && searchValue.trim().length > 0) {
        filter.name = searchValue.trim();
      }

      if (this.dueDateFilter !== null && this.startDate && this.endDate) {
        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);

        startDate.setHours(0);
        startDate.setMinutes(0);
        startDate.setSeconds(0);

        endDate.setHours(23);
        endDate.setMinutes(59);
        endDate.setSeconds(59);

        filter.startDate = startDate.toUTCString();
        filter.endDate = endDate.toUTCString();
      }

      // Copy rather than alias this.activeFilters — see applyNewFilter() for why.
      filter.filters = [...this.activeFilters];

      if (this.standalone) {
        this.onSearchFilterChange.emit(filter);
      } else {
        this.workspaceStore.searchFilter = filter;
        if (!this.workspaceStore.hasRawWorkspace() && this.selectedWorkspace?.id) {
          this.loadWorkspace(this.selectedWorkspace.id);
        }
      }
    }
  }

  private loadWorkspace(workspaceId: string): void {
    if (!workspaceId) {
      return;
    }
    if (this.subscribers.workspaceById) {
      this.subscribers.workspaceById.unsubscribe();
    }
    this.loggerService.traceCaller(this.className, this.loadWorkspace.name, 'getWorkspaceById', workspaceId);
    this.subscribers.workspaceById = this.workspaceStore.$getWorkspaceById(workspaceId).subscribe();
  }

  private applyFilterValues(filterValues: Partial<ISearchFilter>): void {
    if (!filterValues) {
      return;
    }
    if (Array.isArray(filterValues.status) && filterValues.status.length > 0) {
      this.statusFilter.setValue(filterValues.status);
      this.filterService.addFilter({
        field: 'status',
        operator: FilterOperator.EQUALS,
        value: filterValues.status,
        label: 'Status',
      });
    }
    if (Array.isArray(filterValues.nodeType) && filterValues.nodeType.length > 0) {
      this.nodeTypeFilter.setValue(filterValues.nodeType);
      this.filterService.addFilter({
        field: 'nodeType',
        operator: FilterOperator.EQUALS,
        value: filterValues.nodeType,
        label: 'Type',
      });
    }
    if (Array.isArray(filterValues.assignee) && filterValues.assignee.length > 0) {
      this.assigneeFilter.setValue(filterValues.assignee);
      this.filterService.addFilter({
        field: 'assignee',
        operator: FilterOperator.EQUALS,
        value: filterValues.assignee,
        label: 'Assignee',
      });
    }
    if (filterValues.name !== undefined && filterValues.name !== null) {
      this.searchControl.setValue(filterValues.name, { emitEvent: false });
    }
  }

  public getFilterConfig(fieldId: string): IFilterConfig | undefined {
    if (!fieldId) {
      return undefined;
    }
    return this.filterService.getAvailableFilters().find((f) => f.field === fieldId || f.id === fieldId);
  }

  public getActiveMoreFilters(): IFilterConfig[] {
    // Get "more" filters that are currently active
    const activeFields = new Set(this.activeFilters.map((f) => f.field));
    return this.moreFilters.filter((cfg) => activeFields.has(cfg.field));
  }

  public trackByFilterId(index: number, filter: any): string {
    if (!filter) {
      return index.toString();
    }
    return filter.id || filter.field || index.toString();
  }
}
